import * as React from 'react';
import { Navigate } from 'react-router-dom';

import createContext from './create-context';
import { useMe, type MeResponseType } from '@/api-models/user';
import { useSessionStore } from '@/stores/session-store';

interface ProviderValue {
  me: NonNullable<MeResponseType>;
  logout: () => Promise<void>;
  refreshMe: () => void;
}

const [useContext, ReactProvider] = createContext<ProviderValue>();

interface AuthProviderProps {
  children?: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = (props) => {
  const { children } = props;
  const expired = useSessionStore((s) => s.expired);
  const markAuthenticated = useSessionStore((s) => s.markAuthenticated);
  const clearExpired = useSessionStore((s) => s.clearExpired);

  const {
    me,
    isLoading: isLoadingMe,
    mutate: mutateMe,
  } = useMe({
    // The only place that arms the expiry latch. Until this succeeds a 401 is just the
    // ordinary state of a logged-out visitor, not a session dying under someone.
    onSuccess: () => {
      markAuthenticated();
      clearExpired();
    },
  });

  const refreshMe = React.useCallback(() => {
    mutateMe(undefined, {
      revalidate: true,
    });
  }, [mutateMe]);

  const logout = React.useCallback(async () => {
    window.location.href = new URL('/api/auth/logout', window.location.origin).toString();
  }, []);

  if (isLoadingMe) {
    return null;
  }

  // SWR keeps the last good `me` when a revalidation fails, so a session that dies while
  // the tab is open leaves this component happily rendering a logged-in shell over an API
  // that 401s everything. That is what turned one expiry into a permanent retry storm.
  // Unmounting the subtree is what actually stops it — every hook, socket, terminal and
  // file watcher goes with it.
  if (expired) {
    return <Navigate to="/login?expired=1" replace />;
  }

  if (!me) {
    return <Navigate to="/login" />;
  } else {
    return <ReactProvider value={{ me, logout, refreshMe }}>{children}</ReactProvider>;
  }
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = useContext;
